import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { ActionTabs, Icon, v2 } from '@fieldnation/platform-components';

import {
  FieldName,
  NOTE_PLACEHOLDERS,
  Section,
  USAGE_CODES as USAGE_ENUMS,
} from '../PartsFormV3/types.d';
import {
  RETURN_INSTRUCTION_OPTIONS_NON_RETURNABLE,
  RETURN_INSTRUCTION_OPTIONS_RETURNABLE,
  validateAdditionalFields,
} from '../PartsFormV3/utils';
import AdditionalPartFields from '../PartsFormV3/AdditionalPartFields';
import { NEW_PART_EXCLUDED_FIELDS } from '../PartsFormV3/constants';
import Photos from '../PartsFormV3/Photos';
import Accordion from '../ProviderSnapshot/Components/Accordion';
import TrackingField from '../ShipmentForm/components/TrackingField';
import { getCarrierFromTrackingNumber } from '../ShipmentForm/utils';
import styles from './styles.scss';
import { DEFAULT_ADDITIONAL_PART_FIELDS } from './constants';
import {
  isProviderPartsFormV3Valid,
  RETURN_CARRIER_OTHER,
  RETURNABLE_NO,
  RETURNABLE_YES,
} from './utils';
import { ProviderPartsFormV3Props, ProviderPartsFormV3Value } from './types';

export type { ProviderPartsFormV3Props, ProviderPartsFormV3Value };

const { Select, TextArea } = v2;

const DEFAULT_VALUE: ProviderPartsFormV3Value = {
  usageCode: '',
  description: '',
  returnable: '',
  returnInstruction: '',
  returnTracking: '',
  returnCarrier: '',
  returnCarrierOtherName: '',
  note: '',
};

const AccordionSection = ({
  label,
  children,
}: { label: string; children: React.ReactNode }): JSX.Element => (
  <div className={styles['accordion-panel']}>
    <Accordion label={label} value="" showBadge={false} expand border="bottom">
      <div className={styles['accordion-panel-body']}>{children}</div>
    </Accordion>
  </div>
);

const ProviderPartsFormV3 = ({
  value,
  onChange,
  additionalFields = false,
  additionalPartFields,
  partFiles,
  isProvider = false,
}: ProviderPartsFormV3Props): JSX.Element => {
  const providerFormValue = {
    ...(value || {}),
  } as Partial<ProviderPartsFormV3Value> & { partNumber?: string };
  delete providerFormValue.partNumber;
  const [formData, setFormData] = useState<ProviderPartsFormV3Value>({
    ...DEFAULT_VALUE,
    ...providerFormValue,
    usageCode: USAGE_ENUMS.USED,
  });
  const [returnTrackingLink, setReturnTrackingLink] = useState('');
  const configuredAdditionalPartFields = useMemo(
    () => additionalPartFields
      ?.filter((field) => DEFAULT_ADDITIONAL_PART_FIELDS.some(
        (defaultField) => defaultField.field_name === field.field_name,
      ))
      .map((field) => ({ ...field, required: true })),
    [additionalPartFields],
  );
  const hasConfiguredAdditionalPartFields = Boolean(configuredAdditionalPartFields?.length);
  const displayedAdditionalPartFields = hasConfiguredAdditionalPartFields
    ? configuredAdditionalPartFields
    : DEFAULT_ADDITIONAL_PART_FIELDS;
  const isReturnable = formData.returnable === RETURNABLE_YES;
  const isCustomCarrierNameMissing = formData.returnCarrier === RETURN_CARRIER_OTHER
    && !formData.returnCarrierOtherName?.trim();
  const usageSelected = Boolean(formData.usageCode);
  const showDefectivePart = usageSelected && formData.usageCode !== USAGE_ENUMS.NOT_RECEIVED;

  useEffect(() => {
    if (formData.returnTracking) {
      const { trackingLink } = getCarrierFromTrackingNumber(formData.returnTracking);
      setReturnTrackingLink(trackingLink);
    } else {
      setReturnTrackingLink('');
    }
  }, [formData.returnTracking]);

  const returnInstructionLabel = useMemo(() => {
    const options = [
      ...RETURN_INSTRUCTION_OPTIONS_RETURNABLE,
      ...RETURN_INSTRUCTION_OPTIONS_NON_RETURNABLE,
    ];
    return options.find((option) => option.value === formData.returnInstruction)?.label
      || formData.returnInstruction
      || '';
  }, [formData.returnInstruction]);

  const emit = useCallback((next: ProviderPartsFormV3Value) => {
    const baseValid = isProviderPartsFormV3Valid(next);
    const additionalFieldsValid = !additionalFields || !hasConfiguredAdditionalPartFields
      || validateAdditionalFields(configuredAdditionalPartFields, next.additionalPartValues, next.usageCode);
    onChange(next, baseValid && additionalFieldsValid);
  }, [
    onChange,
    additionalFields,
    hasConfiguredAdditionalPartFields,
    configuredAdditionalPartFields,
  ]);

  // Parent controls the Save button. Validate populated edit data on mount as
  // well as after each form update, so an already-complete part can be saved.
  useEffect(() => {
    emit(formData);
  }, [emit, formData]);

  const updateForm = (updated: ProviderPartsFormV3Value) => {
    setFormData(updated);
  };

  const onReturnableToggle = (returnable: typeof RETURNABLE_YES | typeof RETURNABLE_NO) => {
    const instructions = returnable === RETURNABLE_YES
      ? RETURN_INSTRUCTION_OPTIONS_RETURNABLE
      : RETURN_INSTRUCTION_OPTIONS_NON_RETURNABLE;
    updateForm({
      ...formData,
      returnable,
      returnInstruction: instructions[0]?.value || '',
      returnTracking: returnable === RETURNABLE_NO ? '' : formData.returnTracking,
      returnCarrier: returnable === RETURNABLE_NO ? '' : formData.returnCarrier,
      returnCarrierOtherName: returnable === RETURNABLE_NO ? '' : formData.returnCarrierOtherName,
    });
  };

  const updateAdditionalValue = (section: Section, fieldName: FieldName, fieldValue: string) => {
    updateForm({
      ...formData,
      additionalPartValues: {
        ...formData.additionalPartValues,
        [section]: {
          ...formData.additionalPartValues?.[section],
          [fieldName]: fieldValue,
        },
      },
    });
  };

  return (
    <div className={styles['provider-parts-form-v3']}>
      <AccordionSection label="New Part">
        <Select
          label="Usage code"
          options={[{ label: USAGE_ENUMS.USED, value: USAGE_ENUMS.USED }]}
          onChange={() => undefined}
          value={formData.usageCode}
          disabled
        />
        {additionalFields && (
          <AdditionalPartFields
            fields={displayedAdditionalPartFields}
            values={formData.additionalPartValues}
            section="new"
            excludeFields={NEW_PART_EXCLUDED_FIELDS}
            onChange={updateAdditionalValue}
            fieldClassName={styles['input-gap']}
            showRequiredErrors
          />
        )}
        <div className={styles['returnable-section']}>
          <p className={styles['return-question']}>Do you need to return the defective part?</p>
          <ActionTabs
            items={[
              {
                label: RETURNABLE_YES,
                active: isReturnable,
                onClick: () => onReturnableToggle(RETURNABLE_YES),
              },
              {
                label: RETURNABLE_NO,
                active: formData.returnable === RETURNABLE_NO,
                onClick: () => onReturnableToggle(RETURNABLE_NO),
              },
            ]}
          />
        </div>
      </AccordionSection>

      {showDefectivePart && (
        <AccordionSection label="Defective Part">
          {additionalFields && (
            <AdditionalPartFields
              fields={displayedAdditionalPartFields}
              values={formData.additionalPartValues}
              section="defective"
              onChange={updateAdditionalValue}
              fieldClassName={styles['input-gap']}
              showRequiredErrors
            />
          )}
          {returnInstructionLabel && (
            <>
              <p className={styles['section-subheader']}>Return information</p>
              <div className={styles['return-instruction']}>
                <Icon name="time" size={22} />
                <span>
                  {returnInstructionLabel}
                  .
                </span>
              </div>
            </>
          )}
          {isReturnable && (
            <div className={styles['input-gap']}>
              <TrackingField
                inputLabel="Return tracking number"
                value={{
                  name: formData.returnCarrier || '',
                  tracking: formData.returnTracking,
                  other_name: formData.returnCarrierOtherName || '',
                  tracking_link: returnTrackingLink,
                }}
                onChange={(carrierInfo) => {
                  updateForm({
                    ...formData,
                    returnTracking: carrierInfo.tracking,
                    returnCarrier: carrierInfo.name || '',
                    returnCarrierOtherName: carrierInfo.other_name ?? '',
                  });
                  setReturnTrackingLink(carrierInfo.tracking_link || '');
                }}
                disabled={false}
                errors={{ name: isCustomCarrierNameMissing, tracking: false }}
              />
            </div>
          )}
        </AccordionSection>
      )}

      {usageSelected && (
        <AccordionSection label="Optional Details">
          {partFiles && (
            <div className={styles['photos-container']}>
              <Photos
                files={partFiles.files}
                loading={partFiles.loading}
                loadError={partFiles.loadError}
                canUpload={isProvider}
                canAddMore={partFiles.canAddMore}
                onUpload={partFiles.uploadFiles}
                onRequestDelete={partFiles.requestDelete}
                onRestore={partFiles.restore}
                getFreshUrl={partFiles.getFreshUrl}
              />
            </div>
          )}
          <div className={styles['input-gap']}>
            <TextArea
              id="notes"
              label="Notes"
              placeholder={NOTE_PLACEHOLDERS[formData.usageCode]}
              value={formData.note || ''}
              onChange={(event) => updateForm({ ...formData, note: event.target.value })}
              optional
            />
          </div>
        </AccordionSection>
      )}
    </div>
  );
};

export default ProviderPartsFormV3;
